/**
 * The work profile the setup conversation asks for (#1738), as both sides of it need it: the fixed structure, the
 * chips of each question and how a typed answer is read. The API validates and stores the structure
 * (`apps/api/src/onboarding/work-profile.ts`); the portal draws the chips and sends the answers. Both use these, so
 * the chips a person is offered and the answers the server accepts cannot drift apart.
 *
 * No kind of work is named here: the kinds offered are the Catalog teams' own `kindOfWork` keys, passed in.
 */

/** The chat purpose of the setup conversation: a job-scoped conversation on the #1203 mechanism. */
export const SETUP_CHAT_PURPOSE = 'company_setup'

/**
 * The purpose of the conversation that creates a task: the COO interviews the person, and the OS designs the task
 * they agree for the CEO to sign off. Named here so the portal can open one from a link.
 */
export const CREATE_TASK_CHAT_PURPOSE = 'create_task'

/** The answer to the kind-of-work question that is not one of the Catalog's kinds. */
export const SOMETHING_ELSE = 'something_else'

/**
 * The role a person typed before typed roles were matched to a known one (#1742, O13). Still read, so a profile
 * saved that way keeps working; a typed role is now saved as the known role it matched, with the person's words.
 */
export const OTHER_ROLE = 'other'

/** The roles offered as chips, in the order they are offered. */
export const WORK_ROLES = ['founder', 'product_designer', 'engineer', 'marketer'] as const

/** One of the four roles a person can pick. */
export type KnownWorkRole = (typeof WORK_ROLES)[number]

export const TECHNICAL_LEVELS = ['non_technical', 'somewhat_technical', 'technical'] as const

/** The most characters a typed answer keeps, after trimming. */
export const WORK_PROFILE_TEXT_MAX = 200

/**
 * The API's refusal code for a `work_profile` save with no kind of work to build on: the saver's own profile holds
 * none and the answers name none. A save is built from the saver's own profile, never from the step, so this is also
 * what a member taking over setup gets when the member who began it left part-way: the step still holds that
 * member's answers, and the portal's chips, drawn from the step, ask the role or how technical. On this refusal the
 * portal asks the kind of work again, and the member's own answer to it replaces the step.
 */
export const WORK_PROFILE_WORK_FIRST = 'work_profile_work_first'

/** That refusal's sentence: word for word what the API says and the person reads in the setup chat. */
export const WORK_PROFILE_WORK_FIRST_TEXT =
    'Setup asks the kind of work first: say what you would like help with before your role or how technical you are.'

/** Kind of work: a Catalog kind key, or free text. */
export type WorkChoice = { kind: string } | { kind: 'something_else'; text: string }

/**
 * A role: one of the four, picked as a chip; one of the four matched from what the person typed, with their words in
 * `text` (#1742); or, as saved before typed roles were matched, `other` with the typed text.
 */
export type WorkRole = { role: KnownWorkRole; text?: string } | { role: 'other'; text: string }

export type TechnicalLevel = (typeof TECHNICAL_LEVELS)[number]

/** The one fixed structure the `work_profile` step stores, on both paths. */
export interface WorkProfile {
    work: WorkChoice
    role?: WorkRole
    technicalLevel?: TechnicalLevel
}

export interface QuickReply {
    label: string
    value: string
}

/** The question setup is on: the kind of work, then the role, then how technical; null once all three are saved. */
export type SetupQuestion = 'work' | 'role' | 'technicalLevel'

/** How each role chip reads. */
export const WORK_ROLE_LABELS: Readonly<Record<(typeof WORK_ROLES)[number], string>> = {
    founder: 'Founder',
    product_designer: 'Product Designer',
    engineer: 'Engineer',
    marketer: 'Marketer',
}

/**
 * The one reporting-style line each role sets (#1697's Onboarding spec): what the COO's instructions carry and the
 * guided About you screen previews ("Your COO will report in …"). `other`, saved before typed roles were matched to a
 * known role (#1742), keeps the founder's.
 */
export const REPORTING_STYLES: Readonly<Record<WorkRole['role'], string>> = {
    founder: 'decision-first briefs',
    product_designer: 'visual, design-led updates',
    engineer: 'technical, PR-level detail',
    marketer: 'outcome and funnel-focused summaries',
    other: 'decision-first briefs',
}

/** The one reporting-style line for a role. */
export function reportingStyleFor(role: Pick<WorkRole, 'role'>): string {
    return REPORTING_STYLES[role.role]
}

/** How each technical-level chip reads. */
export const TECHNICAL_LEVEL_LABELS: Readonly<Record<TechnicalLevel, string>> = {
    non_technical: 'Not technical',
    somewhat_technical: 'Somewhat technical',
    technical: 'Technical',
}

/** How the free-text chip reads, wherever a setup question offers one. */
export const SOMETHING_ELSE_LABEL = 'Something else'

const SOMETHING_ELSE_REPLY: QuickReply = { label: SOMETHING_ELSE_LABEL, value: SOMETHING_ELSE }

/**
 * The question to ask next, given what is saved: kind of work first, then role, then how technical. Null once all
 * three are saved. Reads a stored answer of any shape, so a missing or malformed part is a question still open.
 */
export function setupQuestion(saved: unknown): SetupQuestion | null {
    const profile = isRecord(saved) ? saved : {}
    if (!isRecord(profile['work'])) {
        return 'work'
    }
    if (!isRecord(profile['role'])) {
        return 'role'
    }
    if (typeof profile['technicalLevel'] !== 'string') {
        return 'technicalLevel'
    }
    return null
}

/** The chips of one question: the kinds of work and "Something else", the roles, or the technical levels. */
export function quickRepliesFor(question: SetupQuestion | null, kinds: readonly string[]): QuickReply[] {
    if (question === 'work') {
        return [...kinds.map(kind => ({ label: kind, value: kind })), SOMETHING_ELSE_REPLY]
    }
    if (question === 'role') {
        return WORK_ROLES.map(role => ({ label: WORK_ROLE_LABELS[role], value: role }))
    }
    if (question === 'technicalLevel') {
        return TECHNICAL_LEVELS.map(level => ({ label: TECHNICAL_LEVEL_LABELS[level], value: level }))
    }
    return []
}

/**
 * The chips of the question the conversation is on, given what is saved: the kinds of work and "Something else",
 * then the roles, then the technical levels. Empty once all three are answered.
 */
export function setupQuickReplies(saved: Partial<WorkProfile>, kinds: readonly string[]): QuickReply[] {
    return quickRepliesFor(setupQuestion(saved), kinds)
}

/** A typed answer to the kind-of-work question: the chip it matches (ignoring case), else `something_else`. */
export function workAnswerFromTyped(typed: string, kinds: readonly string[]): WorkChoice | null {
    const text = typed.trim()
    if (text === '') {
        return null
    }
    const kind = kinds.find(offered => offered.toLowerCase() === text.toLowerCase())
    return kind ? { kind } : { kind: SOMETHING_ELSE, text: keptText(text) }
}

/** A typed answer to the role question: the role whose chip it matches (ignoring case), else `other` with the text. */
export function roleAnswerFromTyped(typed: string): WorkRole | null {
    const text = typed.trim()
    if (text === '') {
        return null
    }
    const role = WORK_ROLES.find(offered => WORK_ROLE_LABELS[offered].toLowerCase() === text.toLowerCase())
    return role ? { role } : { role: OTHER_ROLE, text: keptText(text) }
}

/**
 * Typed text as the profile keeps it: at most `WORK_PROFILE_TEXT_MAX` characters, so a longer reply still saves its
 * start while the whole reply is sent to the COO. The API still refuses raw text over the limit.
 */
function keptText(text: string): string {
    const kept = text.slice(0, WORK_PROFILE_TEXT_MAX)
    // Never keep half of a character the cut went through (a surrogate pair, such as an emoji).
    const last = kept.charCodeAt(kept.length - 1)
    return (last >= 0xd800 && last <= 0xdbff ? kept.slice(0, -1) : kept).trimEnd()
}

/** The `work_profile` answers a chip saves, for the question it was offered on. */
export function setupAnswerFromChip(question: SetupQuestion, value: string): Partial<WorkProfile> {
    if (question === 'work') {
        return { work: { kind: value } }
    }
    if (question === 'role') {
        return { role: { role: value } as WorkRole }
    }
    return { technicalLevel: value as TechnicalLevel }
}

/**
 * The `work_profile` answers typed text saves while `question` is open: the kind of work or the role it names, or
 * the text itself as `something_else` or `other`. Null when the question takes no typed answer (how technical is
 * chips only) or the text is blank: the text is then an ordinary message, and nothing is saved.
 */
export function setupAnswerFromTyped(
    question: SetupQuestion | null,
    typed: string,
    kinds: readonly string[],
): Partial<WorkProfile> | null {
    if (question === 'work') {
        const work = workAnswerFromTyped(typed, kinds)
        return work ? { work } : null
    }
    if (question === 'role') {
        const role = roleAnswerFromTyped(typed)
        return role ? { role } : null
    }
    return null
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The words that point a typed role at a known role. A word matches a whole word of the typed text, or a run of
 * words (`product owner`), ignoring case; on a tie the role listed first wins. A typed role that names none falls
 * back to founder.
 */
const ROLE_WORDS: readonly (readonly [KnownWorkRole, readonly string[]])[] = [
    [
        'founder',
        [
            'founder',
            'co-founder',
            'cofounder',
            'co founder',
            'ceo',
            'owner',
            'business owner',
            'entrepreneur',
            'solopreneur',
            'president',
            'managing director',
            'md',
            'chief executive',
            'proprietor',
        ],
    ],
    [
        'product_designer',
        [
            'designer',
            'design',
            'ux',
            'ui',
            'product manager',
            'pm',
            'product owner',
            'product lead',
            'cpo',
            'illustrator',
            'artist',
            'researcher',
        ],
    ],
    [
        'engineer',
        [
            'engineer',
            'engineering',
            'developer',
            'dev',
            'devops',
            'programmer',
            'coder',
            'cto',
            'architect',
            'sre',
            'software',
            'hacker',
            'data scientist',
            'technical lead',
            'tech lead',
            'frontend',
            'backend',
            'full-stack',
            'fullstack',
        ],
    ],
    [
        'marketer',
        [
            'marketer',
            'marketing',
            'growth',
            'cmo',
            'seo',
            'content',
            'copywriter',
            'writer',
            'social media',
            'brand',
            'sales',
            'salesperson',
            'communications',
            'pr',
            'advertising',
        ],
    ],
]

/**
 * The known role a typed role is closest to (O13): "co-founder", "CEO" or "owner" is a founder, "developer" or
 * "CTO" an engineer, "UX designer" a product designer, "growth lead" a marketer. Null for blank text; anything it
 * cannot place is a founder, the role the product designs for. Deterministic, so the chat, the guided About you
 * screen and the API all match the same words to the same role.
 */
export function closestRoleFromTyped(typed: string): KnownWorkRole | null {
    const text = typed.trim().toLowerCase()
    if (text === '') {
        return null
    }
    // A slash separates words as a space does, so "Designer/Developer" is two roles' words, not one unknown word.
    const words = ` ${text.replace(/[^\p{L}\p{N}+-]+/gu, ' ').trim()} `
    // The longest name found wins, so "product owner" is a product designer although "owner" names a founder.
    let best: { role: KnownWorkRole; length: number } | null = null
    for (const [role, names] of ROLE_WORDS) {
        for (const name of names) {
            if (words.includes(` ${name} `) && (best === null || name.length > best.length)) {
                best = { role, length: name.length }
            }
        }
    }
    return best?.role ?? 'founder'
}
